import {
  DndContext,
  DragOverlay,
  MouseSensor,
  TouchSensor,
  closestCenter,
  pointerWithin,
  useDraggable,
  useDroppable,
  useSensor,
  useSensors,
} from '@dnd-kit/core'
import type { CollisionDetection, DragEndEvent, DragStartEvent } from '@dnd-kit/core'
import { arrayMove } from '@dnd-kit/sortable'
import { Close, Visibility } from '@mui/icons-material'
import {
  Box,
  Button,
  Chip,
  ClickAwayListener,
  Dialog,
  IconButton,
  Popper,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material'
import { type Theme, alpha, darken, lighten } from '@mui/material/styles'
import { chapterSectionKey } from '@shared/chapterSectionsOrder'
import { ELEMENTS_ROOT_TAG_ID } from '@shared/tags/canonicalElementsTags'
import type { TagDefinition } from '@shared/tags/tagRegistry'
import { type ReactNode, useCallback, useEffect, useMemo, useRef, useState } from 'react'
import toast from 'react-hot-toast'

import SearchPreviewPanel from '../../../../components/search/SearchPreviewPanel'
import { type Book, type ContentFile, apiService } from '../../../../services/api'
import { logger } from '../../../../utils/logger'
import { sectionTagsAnnotationId } from '../../../../utils/sectionTagsAnnotationId'
import type { ChapterGroup, ChapterSectionEntry } from '../../hooks/useChapterSectionsBook'
import { useSectionPreview } from '../../hooks/useSectionPreview'

type TagMap = Record<string, TagDefinition>

const NOELEM = '__noelem__'
const NOCHAP = '__nochap__'
const CELL_SEP = '|::|'
// dnd id namespaces for the per-element panel: panel rows (panel:<key>) drag onto chapter
// row-labels (chaprow:<id>) to assign; unassigning is done by the per-row ✕.
const PANEL_PREFIX = 'panel:'
const CHAPROW_PREFIX = 'chaprow:'

const COL_LABEL_W = 100
const COL_UNCH_W = 70
const COL_CHAP_W = 92
const ROW_H = 34
const CHIP_MAX_W = 122

// Surface hierarchy so things separate against the dark cells: cells = background.default,
// sticky headers/labels = a small lift, hover popovers (tooltips, expanded cells) = a bigger lift.
const labelBg = (theme: Theme) =>
  theme.palette.mode === 'dark'
    ? lighten(theme.palette.background.default, 0.06)
    : darken(theme.palette.background.default, 0.04)
const raisedBg = (theme: Theme) =>
  theme.palette.mode === 'dark'
    ? lighten(theme.palette.background.default, 0.12)
    : darken(theme.palette.background.default, 0.07)

// Raised, readable hover popovers (lifted surface + shadow, unlike the default dark tooltip).
const tooltipSlotProps = {
  tooltip: {
    sx: {
      maxWidth: 'min(92vw, 420px)',
      bgcolor: raisedBg,
      color: 'text.primary',
      border: '1px solid',
      borderColor: 'divider',
      boxShadow: 6,
      p: 1,
    },
  },
}

// Cell identity is always (elementId, chapterId), independent of which axis is rendered as rows.
const cellId = (elementId: string, chapterId: string): string => `${elementId}${CELL_SEP}${chapterId}`
const parseCellId = (id: string): { elementId: string; chapterId: string } | null => {
  const idx = id.indexOf(CELL_SEP)
  if (idx === -1) {
    return null
  }
  return { elementId: id.slice(0, idx), chapterId: id.slice(idx + CELL_SEP.length) }
}

interface ChapterCol {
  id: string
  title: string
  sections: string[]
}

interface MatrixDraft {
  chapters: ChapterCol[]
  unattachedChapter: string[]
  /** sectionKey -> elementId (null = no element). */
  elementOf: Record<string, string | null>
}

function cloneDraft(d: MatrixDraft): MatrixDraft {
  return {
    chapters: d.chapters.map((c) => ({ ...c, sections: [...c.sections] })),
    unattachedChapter: [...d.unattachedChapter],
    elementOf: { ...d.elementOf },
  }
}

/**
 * Move keys to a target chapter (NOCHAP = unattached), preserving reading order for keys that
 * are already there. Returns a fresh draft; leaves elementOf untouched.
 */
function moveKeysToChapter(d: MatrixDraft, keys: string[], targetChap: string): MatrixDraft {
  const next = cloneDraft(d)
  const moving = keys.filter((k) => {
    for (const c of next.chapters) {
      if (c.sections.includes(k)) {
        return c.id !== targetChap
      }
    }
    return targetChap !== NOCHAP
  })
  const movingSet = new Set(moving)
  for (const c of next.chapters) {
    c.sections = c.sections.filter((k) => !movingSet.has(k))
  }
  next.unattachedChapter = next.unattachedChapter.filter((k) => !movingSet.has(k))
  if (moving.length > 0) {
    if (targetChap === NOCHAP) {
      next.unattachedChapter.push(...moving)
    } else {
      const c = next.chapters.find((ch) => ch.id === targetChap)
      if (c) {
        c.sections.push(...moving)
      }
    }
  }
  return next
}

function elementFamilyIds(tags: TagMap): Set<string> {
  const ids = new Set<string>()
  for (const [id, def] of Object.entries(tags)) {
    if ((def.parentIds || []).includes(ELEMENTS_ROOT_TAG_ID)) {
      ids.add(id)
    }
  }
  return ids
}

/** A draggable compact section chip; click selects, the hover eye opens the preview. */
function MatrixChip({
  sectionKey,
  label,
  selected,
  draggedAway,
  onSelect,
  onOpen,
}: {
  sectionKey: string
  label: string
  selected: boolean
  draggedAway: boolean
  onSelect: (key: string, shiftKey: boolean, metaKey: boolean) => void
  onOpen: () => void
}) {
  const { attributes, listeners, setNodeRef, isDragging } = useDraggable({ id: sectionKey })
  return (
    <Box
      ref={setNodeRef}
      {...attributes}
      {...listeners}
      onClick={(e) => onSelect(sectionKey, e.shiftKey, e.metaKey || e.ctrlKey)}
      sx={{
        display: 'flex',
        alignItems: 'center',
        gap: 0.25,
        flexShrink: 0,
        maxWidth: CHIP_MAX_W,
        overflow: 'hidden',
        px: 0.75,
        py: 0.25,
        borderRadius: 0.75,
        border: '1px solid',
        borderColor: selected ? 'primary.main' : 'divider',
        bgcolor: selected ? 'action.selected' : 'background.default',
        cursor: 'grab',
        touchAction: 'manipulation',
        opacity: isDragging || draggedAway ? 0.3 : 1,
        '&:hover': { borderColor: selected ? 'primary.main' : 'text.secondary' },
        '&:hover .chip-eye': { opacity: 1 },
      }}
    >
      <Typography
        sx={{
          fontSize: 11.5,
          lineHeight: 1.3,
          minWidth: 0,
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          whiteSpace: 'nowrap',
        }}
      >
        {label}
      </Typography>
      <IconButton
        className="chip-eye"
        size="small"
        onPointerDown={(e) => e.stopPropagation()}
        onClick={(e) => {
          e.stopPropagation()
          onOpen()
        }}
        aria-label="תצוגת מקטע"
        sx={{ p: 0.125, opacity: 0, flexShrink: 0, transition: 'opacity 0.15s' }}
      >
        <Visibility sx={{ fontSize: 13 }} />
      </IconButton>
    </Box>
  )
}

/**
 * A droppable matrix cell, fixed to a single row height. Its chips live in an absolutely
 * positioned layer clipped to one line; when the cell holds more than fits, hovering expands
 * that layer into a floating panel (wrapping all chips) without disturbing the grid layout.
 */
function MatrixCell({ id, expandable, children }: { id: string; expandable: boolean; children: ReactNode }) {
  const { setNodeRef, isOver } = useDroppable({ id })
  return (
    <Box
      ref={setNodeRef}
      sx={{
        position: 'relative',
        height: ROW_H,
        borderInlineEnd: '1px solid',
        borderBottom: '1px solid',
        borderColor: 'divider',
        bgcolor: isOver ? 'action.hover' : 'transparent',
        ...(expandable && {
          '&:hover .cell-content': {
            flexWrap: 'wrap',
            height: 'auto',
            minHeight: '100%',
            maxHeight: 360,
            overflowY: 'auto',
            overflowX: 'hidden',
            minWidth: 'max(100%, 240px)',
            zIndex: 20,
            bgcolor: raisedBg,
            border: '1px solid',
            borderColor: 'primary.main',
            borderRadius: 1,
            boxShadow: 6,
          },
        }),
      }}
    >
      <Box
        className="cell-content"
        sx={{
          position: 'absolute',
          top: 0,
          insetInlineStart: 0,
          width: '100%',
          height: '100%',
          p: 0.5,
          display: 'flex',
          flexWrap: 'nowrap',
          gap: 0.5,
          alignItems: 'flex-start',
          alignContent: 'flex-start',
          overflow: 'hidden',
        }}
      >
        {children}
      </Box>
    </Box>
  )
}

/**
 * An element column header: click to rename inline, drag onto another element to reorder. Adds no
 * minimum width — the inline editor fills the existing cell and there is no separate drag handle —
 * so narrow element columns stay narrow.
 */
function ElementAxisItem({
  id,
  name,
  count,
  onCountClick,
  containerSx,
  editing,
  editValue,
  isActiveDrag,
  active,
  onStartEdit,
  onChangeEdit,
  onCommitEdit,
  onCancelEdit,
}: {
  id: string
  name: string
  count: number
  onCountClick: (id: string, anchor: HTMLElement) => void
  containerSx: Record<string, unknown>
  editing: boolean
  editValue: string
  isActiveDrag: boolean
  active: boolean
  onStartEdit: (id: string, name: string) => void
  onChangeEdit: (value: string) => void
  onCommitEdit: () => void
  onCancelEdit: () => void
}) {
  const { setNodeRef: setDragRef, attributes, listeners } = useDraggable({ id })
  const { setNodeRef: setDropRef, isOver } = useDroppable({ id })
  const setRefs = useCallback(
    (node: HTMLElement | null) => {
      setDragRef(node)
      setDropRef(node)
    },
    [setDragRef, setDropRef],
  )
  const dragProps = editing ? {} : { ...attributes, ...listeners }
  return (
    <Box
      ref={setRefs}
      {...dragProps}
      sx={{
        ...containerSx,
        cursor: editing ? 'default' : 'grab',
        opacity: isActiveDrag ? 0.4 : 1,
        outline: isOver ? '2px solid' : 'none',
        outlineColor: 'primary.main',
        outlineOffset: '-2px',
        ...(active && {
          bgcolor: (theme: Theme) => alpha(theme.palette.primary.main, 0.16),
        }),
      }}
    >
      {editing ? (
        <TextField
          autoFocus
          fullWidth
          variant="standard"
          size="small"
          value={editValue}
          onChange={(e) => onChangeEdit(e.target.value)}
          onBlur={onCommitEdit}
          onKeyDown={(e) => {
            if (e.key === 'Enter') {
              e.preventDefault()
              e.stopPropagation()
              onCommitEdit()
            } else if (e.key === 'Escape') {
              e.stopPropagation()
              onCancelEdit()
            }
          }}
          inputProps={{ style: { fontSize: 12.5, fontWeight: 600, padding: 0 } }}
        />
      ) : (
        <>
          <Typography
            onClick={() => onStartEdit(id, name)}
            title={name}
            sx={{
              minWidth: 0,
              fontSize: 12.5,
              fontWeight: 600,
              color: active ? 'primary.main' : undefined,
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
              cursor: 'text',
              '&:hover': { textDecoration: 'underline' },
            }}
          >
            {name}
          </Typography>
          <Typography
            data-element-id={id}
            onPointerDown={(e) => e.stopPropagation()}
            onClick={(e) => {
              e.stopPropagation()
              onCountClick(id, e.currentTarget)
            }}
            sx={{
              fontSize: 11,
              color: 'text.secondary',
              flexShrink: 0,
              cursor: 'pointer',
              borderRadius: 0.5,
              px: 0.5,
              width: 'fit-content',
              '&:hover': { bgcolor: 'action.hover', color: 'text.primary' },
            }}
          >
            {count}
          </Typography>
        </>
      )}
    </Box>
  )
}

/** Defers building a tooltip's body until the tooltip actually opens (MUI mounts the title lazily). */
function LazyTooltipBody({ build }: { build: () => ReactNode }) {
  return <>{build()}</>
}

/** A chapter row label that is also a drop target for panel chips (drop → assign to this chapter). */
function ChapterRowLabel({
  chapterId,
  label,
  count,
  tooltip,
  labelSx,
}: {
  chapterId: string
  label: string
  count: number
  tooltip: ReactNode
  labelSx: Record<string, unknown>
}) {
  const { setNodeRef, isOver } = useDroppable({ id: CHAPROW_PREFIX + chapterId })
  return (
    <Tooltip title={tooltip} placement="left-start" enterDelay={250} slotProps={tooltipSlotProps}>
      <Box
        ref={setNodeRef}
        sx={{
          ...labelSx,
          ...(isOver && {
            bgcolor: 'action.hover',
            outline: '2px solid',
            outlineColor: 'primary.main',
            outlineOffset: '-2px',
          }),
        }}
      >
        <Typography
          sx={{
            flex: 1,
            minWidth: 0,
            fontSize: 12.5,
            fontWeight: 500,
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
          }}
        >
          {label}
        </Typography>
        <Typography sx={{ fontSize: 11, color: 'text.secondary', flexShrink: 0 }}>{count}</Typography>
      </Box>
    </Tooltip>
  )
}

/** A row in the per-element panel: a draggable section showing its source file and its chapter. */
function PanelRow({
  sectionKey,
  title,
  sourceLabel,
  chapterText,
  selected,
  draggedAway,
  onSelect,
  onOpen,
  onUnassign,
}: {
  sectionKey: string
  title: string
  sourceLabel: string
  chapterText: string | null
  selected: boolean
  draggedAway: boolean
  onSelect: (key: string, shiftKey: boolean, metaKey: boolean) => void
  onOpen: () => void
  onUnassign: () => void
}) {
  const { attributes, listeners, setNodeRef, isDragging } = useDraggable({ id: PANEL_PREFIX + sectionKey })
  return (
    <Box
      ref={setNodeRef}
      {...attributes}
      {...listeners}
      onClick={(e) => onSelect(sectionKey, e.shiftKey, e.metaKey || e.ctrlKey)}
      sx={{
        display: 'flex',
        alignItems: 'center',
        gap: 0.5,
        px: 0.75,
        py: 0.4,
        borderRadius: 0.75,
        border: '1px solid',
        borderColor: selected ? 'primary.main' : 'divider',
        bgcolor: selected ? 'action.selected' : 'background.default',
        cursor: 'grab',
        touchAction: 'manipulation',
        opacity: isDragging || draggedAway ? 0.3 : 1,
        '&:hover': { borderColor: selected ? 'primary.main' : 'text.secondary' },
        '&:hover .row-eye': { opacity: 1 },
      }}
    >
      <IconButton
        className="row-eye"
        size="small"
        onPointerDown={(e) => e.stopPropagation()}
        onClick={(e) => {
          e.stopPropagation()
          onOpen()
        }}
        aria-label="תצוגת מקטע"
        sx={{ p: 0.125, opacity: 0, flexShrink: 0, transition: 'opacity 0.15s' }}
      >
        <Visibility sx={{ fontSize: 13 }} />
      </IconButton>
      <Typography
        sx={{
          flex: 1,
          minWidth: 0,
          fontSize: 12.5,
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          whiteSpace: 'nowrap',
        }}
      >
        {title}
      </Typography>
      <Typography
        title={sourceLabel}
        sx={{
          fontSize: 10.5,
          color: 'text.secondary',
          maxWidth: 100,
          flexShrink: 0,
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          whiteSpace: 'nowrap',
        }}
      >
        {sourceLabel}
      </Typography>
      {chapterText && (
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 0.25,
            flexShrink: 0,
            px: 0.5,
            py: 0.125,
            borderRadius: 0.5,
            bgcolor: 'action.selected',
          }}
        >
          <Typography sx={{ fontSize: 10.5, fontWeight: 600 }}>{chapterText}</Typography>
          <IconButton
            size="small"
            onPointerDown={(e) => e.stopPropagation()}
            onClick={(e) => {
              e.stopPropagation()
              onUnassign()
            }}
            aria-label="ביטול שיבוץ"
            sx={{ p: 0, '&:hover': { color: 'error.main' } }}
          >
            <Close sx={{ fontSize: 12 }} />
          </IconButton>
        </Box>
      )}
    </Box>
  )
}

interface ElementChapterMatrixDialogProps {
  open: boolean
  onClose: () => void
  entries: ChapterSectionEntry[]
  entryByKey: Map<string, ChapterSectionEntry>
  chapterGroups: ChapterGroup[]
  bookId?: string
  bookDirection?: 'ltr' | 'rtl'
  tagRegistry?: TagMap
  onSectionTagsPersisted?: () => void
  /** Persists element name/order edits to the tag registry. */
  onRegistrySaved?: (tags: TagMap) => void
  /** Persists chapter membership/order (book.settings.chapters). */
  onChaptersSave: (chapterDefs: Array<{ id: string; title: string; chapterSections: string[] }>) => Promise<void>
  book?: Book | null
  onSectionContentSaved?: (filename: string, updatedFile: ContentFile) => void
}

/**
 * Two-axis sorting matrix: chapter rows × element columns. Each section sits in the cell (its
 * element tag, its chapter membership); one drag sets both. Element assignment persists as section
 * tags (like the elements board); chapter membership persists via onChaptersSave
 * (book.settings.chapters). Elements can be renamed/reordered here; chapter CRUD stays in the
 * chapters dialog.
 */
export default function ElementChapterMatrixDialog({
  open,
  onClose,
  entries,
  entryByKey,
  chapterGroups,
  bookId,
  bookDirection = 'ltr',
  tagRegistry,
  onSectionTagsPersisted,
  onRegistrySaved,
  onChaptersSave,
  book,
  onSectionContentSaved,
}: ElementChapterMatrixDialogProps) {
  const familyIds = useMemo(() => elementFamilyIds(tagRegistry ?? {}), [tagRegistry])

  // Element rows/cols are a draft (rename + reorder) seeded once from the registry; persisted to
  // the tag registry on save. Add/delete still belongs to the elements board.
  const [elementList, setElementList] = useState<{ id: string; name: string }[]>(() => {
    const tags = tagRegistry ?? {}
    return Object.entries(tags)
      .filter(([, def]) => (def.parentIds || []).includes(ELEMENTS_ROOT_TAG_ID))
      .map(([id, def]) => ({ id, name: def.name, order: typeof def.order === 'number' ? def.order : 0 }))
      .sort((a, b) => a.order - b.order || a.name.localeCompare(b.name, 'he'))
      .map(({ id, name }) => ({ id, name }))
  })
  const elementIdSet = useMemo(() => new Set(elementList.map((e) => e.id)), [elementList])

  const allKeys = useMemo(() => entries.map((e) => chapterSectionKey(e.chapter.filename, e.headingBlockId)), [entries])

  const [draft, setDraft] = useState<MatrixDraft>(() => {
    const known = new Set(allKeys)
    const chapters: ChapterCol[] = chapterGroups
      .filter((g) => g.chapter.id !== '__unattached__')
      .map((g) => ({
        id: g.chapter.id,
        title: g.chapter.title,
        sections: g.chapter.chapterSections.filter((k) => known.has(k)),
      }))
    const unattachedGroup = chapterGroups.find((g) => g.chapter.id === '__unattached__')
    const unattachedChapter = (unattachedGroup?.chapter.chapterSections ?? []).filter((k) => known.has(k))
    const elementOf: Record<string, string | null> = {}
    for (const entry of entries) {
      const key = chapterSectionKey(entry.chapter.filename, entry.headingBlockId)
      elementOf[key] = entry.tagIds.find((t) => familyIds.has(t)) ?? null
    }
    return { chapters, unattachedChapter, elementOf }
  })

  const [chaptersDirty, setChaptersDirty] = useState(false)
  const [elementsDirty, setElementsDirty] = useState(false)
  const [registryDirty, setRegistryDirty] = useState(false)
  const [editingId, setEditingId] = useState<string | null>(null)
  const [editValue, setEditValue] = useState('')
  const [activeId, setActiveId] = useState<string | null>(null)
  const [saving, setSaving] = useState(false)
  const dirty = chaptersDirty || elementsDirty || registryDirty || editingId !== null

  const {
    previewKey,
    previewAst,
    previewLoading,
    previewError,
    previewTitle,
    previewDirty,
    previewSaving,
    previewRecentlySaved,
    openPreview,
    closePreview,
    onPreviewChange,
    onPreviewSave,
  } = useSectionPreview({ open, bookId, entryByKey, onSectionContentSaved })

  // Chapters are the rows, elements the columns. Unattached (NOCHAP) sections have no row — they
  // live in the per-element panel; their cell color is reached by dragging panel chips onto rows.
  const rowIds = useMemo(() => draft.chapters.map((c) => c.id), [draft.chapters])
  const columnIds = useMemo(() => [...elementList.map((e) => e.id), NOELEM], [elementList])

  const elementOfKey = useCallback((key: string): string | null => draft.elementOf[key] ?? null, [draft.elementOf])

  const chapterOfKey = useCallback(
    (key: string): string => {
      for (const c of draft.chapters) {
        if (c.sections.includes(key)) {
          return c.id
        }
      }
      return NOCHAP
    },
    [draft.chapters],
  )

  const cellKeysFor = useCallback(
    (elementId: string, chapterId: string): string[] => {
      const list =
        chapterId === NOCHAP
          ? draft.unattachedChapter
          : (draft.chapters.find((c) => c.id === chapterId)?.sections ?? [])
      const wantElem = elementId === NOELEM ? null : elementId
      return list.filter((k) => (draft.elementOf[k] ?? null) === wantElem)
    },
    [draft],
  )

  // Linear key order (row-major) for shift-range selection. Includes NOCHAP (off-grid, in the
  // per-element panel) so a shift-range can still span unattached sections.
  const allKeysInOrder = useMemo(() => {
    const keys: string[] = []
    for (const chapterId of [...rowIds, NOCHAP]) {
      for (const elementId of columnIds) {
        keys.push(...cellKeysFor(elementId, chapterId))
      }
    }
    return keys
  }, [rowIds, columnIds, cellKeysFor])

  // --- per-element panel (replaces the unassigned row): click a column count to open ---
  const [panelElementId, setPanelElementId] = useState<string | null>(null)
  const [panelAnchorEl, setPanelAnchorEl] = useState<HTMLElement | null>(null)
  const openPanel = useCallback((id: string, anchor: HTMLElement) => {
    setPanelElementId(id)
    setPanelAnchorEl(anchor)
  }, [])
  const closePanel = useCallback(() => setPanelElementId(null), [])
  const unassignKey = useCallback((key: string) => {
    setDraft((prev) => moveKeysToChapter(prev, [key], NOCHAP))
    setChaptersDirty(true)
  }, [])

  // Step the open panel to the adjacent element column and re-anchor to its count cell.
  const stepPanel = useCallback(
    (delta: number) => {
      if (!panelElementId) {
        return
      }
      const idx = columnIds.indexOf(panelElementId)
      if (idx === -1) {
        return
      }
      const nextIdx = Math.min(Math.max(idx + delta, 0), columnIds.length - 1)
      if (nextIdx === idx) {
        return
      }
      const nextId = columnIds[nextIdx]
      setPanelElementId(nextId)
      const node = document.querySelector<HTMLElement>(`[data-element-id="${nextId}"]`)
      if (node) {
        node.scrollIntoView({ block: 'nearest', inline: 'center' })
        setPanelAnchorEl(node)
      }
    },
    [panelElementId, columnIds],
  )

  // Left/right arrows move the open panel between element columns (direction-aware).
  useEffect(() => {
    if (!panelElementId || previewKey || editingId) {
      return
    }
    const handler = (e: KeyboardEvent) => {
      if (e.key !== 'ArrowLeft' && e.key !== 'ArrowRight') {
        return
      }
      const t = e.target as HTMLElement | null
      if (t && (t.tagName === 'INPUT' || t.tagName === 'TEXTAREA' || t.isContentEditable)) {
        return
      }
      e.preventDefault()
      const rtl = bookDirection === 'rtl'
      const delta = e.key === 'ArrowRight' ? (rtl ? -1 : 1) : rtl ? 1 : -1
      stepPanel(delta)
    }
    window.addEventListener('keydown', handler)
    return () => window.removeEventListener('keydown', handler)
  }, [panelElementId, previewKey, editingId, bookDirection, stepPanel])

  // --- multi-select (mirrors the elements board) ---
  const [selectedKeys, setSelectedKeys] = useState<Set<string>>(new Set())
  const lastClickedKeyRef = useRef<string | null>(null)
  const draggedKeysRef = useRef<string[]>([])

  const toggleSelection = useCallback(
    (key: string, shiftKey: boolean, metaKey: boolean) => {
      setSelectedKeys((prev) => {
        const next = new Set(prev)
        if (shiftKey && lastClickedKeyRef.current) {
          const from = allKeysInOrder.indexOf(lastClickedKeyRef.current)
          const to = allKeysInOrder.indexOf(key)
          if (from !== -1 && to !== -1) {
            for (let i = Math.min(from, to); i <= Math.max(from, to); i++) {
              next.add(allKeysInOrder[i])
            }
          }
        } else if (metaKey) {
          if (next.has(key)) {
            next.delete(key)
          } else {
            next.add(key)
          }
        } else if (next.size === 1 && next.has(key)) {
          next.clear()
        } else {
          next.clear()
          next.add(key)
        }
        lastClickedKeyRef.current = key
        return next
      })
    },
    [allKeysInOrder],
  )

  const clearSelection = useCallback(() => setSelectedKeys(new Set()), [])

  const getDraggedKeys = useCallback(
    (activeKey: string): string[] =>
      selectedKeys.has(activeKey) && selectedKeys.size > 1
        ? allKeysInOrder.filter((k) => selectedKeys.has(k))
        : [activeKey],
    [selectedKeys, allKeysInOrder],
  )

  // Esc clears selection first (capture phase); an Esc with nothing selected closes the dialog.
  useEffect(() => {
    if (!open) {
      return
    }
    const handler = (e: KeyboardEvent) => {
      if (e.key !== 'Escape' || previewKey || editingId) {
        return
      }
      if (panelElementId) {
        e.stopPropagation()
        setPanelElementId(null)
        return
      }
      if (selectedKeys.size > 0) {
        e.stopPropagation()
        clearSelection()
      }
    }
    window.addEventListener('keydown', handler, true)
    return () => window.removeEventListener('keydown', handler, true)
  }, [open, previewKey, editingId, panelElementId, selectedKeys, clearSelection])

  const sensors = useSensors(
    useSensor(MouseSensor, { activationConstraint: { distance: 5 } }),
    useSensor(TouchSensor, { activationConstraint: { delay: 300, tolerance: 5 } }),
  )

  // Scope droppables to the drag kind (element headers reorder among themselves; section chips
  // target cells), then pointerWithin → closestCenter.
  const detectCollisions = useCallback<CollisionDetection>(
    (args) => {
      const id = String(args.active.id)
      const isElement = elementIdSet.has(id)
      const isPanel = id.startsWith(PANEL_PREFIX)
      const scoped = args.droppableContainers.filter((c) => {
        const cid = String(c.id)
        if (isElement) {
          return elementIdSet.has(cid)
        }
        if (isPanel) {
          return cid.startsWith(CHAPROW_PREFIX)
        }
        return !elementIdSet.has(cid) && !cid.startsWith(CHAPROW_PREFIX)
      })
      const pointer = pointerWithin({ ...args, droppableContainers: scoped })
      if (pointer.length > 0) {
        return closestCenter({ ...args, droppableContainers: scoped.filter((c) => pointer.some((p) => p.id === c.id)) })
      }
      // Panel chips assign only when actually over a chapter label / the unassign zone — no
      // closestCenter snap, so dropping in empty grid space is a no-op rather than a surprise.
      if (isPanel) {
        return []
      }
      return closestCenter({ ...args, droppableContainers: scoped })
    },
    [elementIdSet],
  )

  const onDragStart = useCallback(
    (e: DragStartEvent) => {
      const activeKey = String(e.active.id)
      setActiveId(activeKey)
      if (elementIdSet.has(activeKey)) {
        draggedKeysRef.current = []
        return
      }
      const realKey = activeKey.startsWith(PANEL_PREFIX) ? activeKey.slice(PANEL_PREFIX.length) : activeKey
      if (!selectedKeys.has(realKey)) {
        clearSelection()
        draggedKeysRef.current = [realKey]
      } else {
        draggedKeysRef.current = getDraggedKeys(realKey)
      }
    },
    [elementIdSet, selectedKeys, clearSelection, getDraggedKeys],
  )

  const onDragEnd = useCallback(
    (e: DragEndEvent) => {
      const { active, over } = e
      setActiveId(null)
      const activeKey = String(active.id)

      // Element reorder (header/label dragged onto another element).
      if (elementIdSet.has(activeKey)) {
        draggedKeysRef.current = []
        const overId = over ? String(over.id) : null
        if (overId && elementIdSet.has(overId) && overId !== activeKey) {
          setElementList((prev) => {
            const from = prev.findIndex((el) => el.id === activeKey)
            const to = prev.findIndex((el) => el.id === overId)
            if (from === -1 || to === -1 || from === to) {
              return prev
            }
            return arrayMove(prev, from, to)
          })
          setRegistryDirty(true)
        }
        return
      }

      const isPanel = activeKey.startsWith(PANEL_PREFIX)
      const draggedKeys = draggedKeysRef.current.length
        ? [...draggedKeysRef.current]
        : [isPanel ? activeKey.slice(PANEL_PREFIX.length) : activeKey]
      draggedKeysRef.current = []
      if (!over) {
        return
      }
      const overId = String(over.id)

      // Panel chip → chapter row label (assign to that chapter) or the unassign zone (NOCHAP).
      // Element membership is left untouched — only the chapter changes.
      if (isPanel) {
        if (!overId.startsWith(CHAPROW_PREFIX)) {
          return
        }
        const tc = overId.slice(CHAPROW_PREFIX.length)
        if (!draggedKeys.some((k) => chapterOfKey(k) !== tc)) {
          return
        }
        setDraft((prev) => moveKeysToChapter(prev, draggedKeys, tc))
        setChaptersDirty(true)
        return
      }

      // Grid chip → cell (sets both element and chapter).
      const target = parseCellId(overId)
      if (!target) {
        return
      }
      const targetElem = target.elementId === NOELEM ? null : target.elementId
      const targetChap = target.chapterId
      const chapterChange = draggedKeys.some((k) => chapterOfKey(k) !== targetChap)
      const elementChange = draggedKeys.some((k) => elementOfKey(k) !== targetElem)
      if (!chapterChange && !elementChange) {
        return
      }
      setDraft((prev) => {
        const next = moveKeysToChapter(prev, draggedKeys, targetChap)
        for (const k of draggedKeys) {
          next.elementOf[k] = targetElem
        }
        return next
      })
      if (chapterChange) {
        setChaptersDirty(true)
      }
      if (elementChange) {
        setElementsDirty(true)
      }
    },
    [elementIdSet, chapterOfKey, elementOfKey],
  )

  const startEdit = useCallback((id: string, name: string) => {
    setEditingId(id)
    setEditValue(name)
  }, [])

  const commitEdit = useCallback(() => {
    const id = editingId
    setEditingId(null)
    if (!id) {
      return
    }
    const name = editValue.trim()
    setElementList((prev) => {
      const el = prev.find((e) => e.id === id)
      if (!el || !name || el.name === name) {
        return prev
      }
      return prev.map((e) => (e.id === id ? { ...e, name } : e))
    })
    setRegistryDirty(true)
  }, [editingId, editValue])

  const cancelEdit = useCallback(() => setEditingId(null), [])

  const handleClose = useCallback(() => {
    if (dirty && !window.confirm('לצאת בלי לשמור? השינויים שעשית יימחקו.')) {
      return
    }
    onClose()
  }, [dirty, onClose])

  const handleSave = useCallback(async () => {
    if (!bookId || saving) {
      return
    }
    setSaving(true)
    try {
      let list = elementList
      if (editingId) {
        const name = editValue.trim()
        if (name) {
          list = list.map((e) => (e.id === editingId ? { ...e, name } : e))
        }
        setEditingId(null)
      }

      if (registryDirty || editingId) {
        const nextTags: TagMap = { ...(tagRegistry ?? {}) }
        for (const id of familyIds) {
          delete nextTags[id]
        }
        list.forEach((e, i) => {
          nextTags[e.id] = { name: e.name, parentIds: [ELEMENTS_ROOT_TAG_ID], order: i + 1 }
        })
        const res = await apiService.updateTagRegistry(bookId, { version: 2, tags: nextTags })
        onRegistrySaved?.((res.data?.tags ?? nextTags) as TagMap)
      }

      if (elementsDirty) {
        let changed = false
        for (const entry of entries) {
          const key = chapterSectionKey(entry.chapter.filename, entry.headingBlockId)
          const target = draft.elementOf[key] ?? null
          const persisted = entry.tagIds.find((t) => familyIds.has(t)) ?? null
          if (target === persisted) {
            continue
          }
          const nextTags = [...entry.tagIds.filter((t) => !familyIds.has(t)), ...(target ? [target] : [])]
          await apiService.updateAnnotation(bookId, entry.chapter.filename, {
            id: sectionTagsAnnotationId(entry.headingBlockId),
            tags: nextTags,
            anchor: { type: 'block', blockId: entry.headingBlockId },
            createdAt: new Date(),
          })
          changed = true
        }
        if (changed) {
          onSectionTagsPersisted?.()
        }
      }
      if (chaptersDirty) {
        await onChaptersSave(draft.chapters.map((c) => ({ id: c.id, title: c.title, chapterSections: c.sections })))
      }
      onClose()
    } catch (err) {
      logger.error('Failed to save elements×chapters matrix', err)
      toast.error('השמירה נכשלה')
    } finally {
      setSaving(false)
    }
  }, [
    bookId,
    saving,
    elementsDirty,
    chaptersDirty,
    registryDirty,
    editingId,
    editValue,
    elementList,
    tagRegistry,
    onRegistrySaved,
    draft.chapters,
    draft.elementOf,
    entries,
    familyIds,
    onSectionTagsPersisted,
    onChaptersSave,
    onClose,
  ])

  const draggedSet = new Set<string>(activeId && !elementIdSet.has(activeId) ? draggedKeysRef.current : [])
  const activeSectionKey =
    activeId && !elementIdSet.has(activeId)
      ? activeId.startsWith(PANEL_PREFIX)
        ? activeId.slice(PANEL_PREFIX.length)
        : activeId
      : null
  const activeEntry = activeSectionKey ? entryByKey.get(activeSectionKey) : null
  const activeElement = activeId && elementIdSet.has(activeId) ? elementList.find((e) => e.id === activeId) : null
  const dragCount = draggedKeysRef.current.length

  const chapterLabel = (chapterId: string): string => {
    if (chapterId === NOCHAP) {
      return 'לא משובץ'
    }
    // Titles are placeholders for now — show just the chapter number (used in the row labels and the tooltip).
    return `פרק ${draft.chapters.findIndex((c) => c.id === chapterId) + 1}`
  }
  const elementLabel = (elementId: string): string =>
    elementId === NOELEM ? 'ללא יסוד' : (elementList.find((el) => el.id === elementId)?.name ?? elementId)

  const chapterCount = (chapterId: string): number =>
    chapterId === NOCHAP
      ? draft.unattachedChapter.length
      : (draft.chapters.find((c) => c.id === chapterId)?.sections.length ?? 0)
  const elementCount = (elementId: string): number => {
    const wantElem = elementId === NOELEM ? null : elementId
    return allKeys.filter((k) => (draft.elementOf[k] ?? null) === wantElem).length
  }

  // Rows are chapters, columns are elements.
  const rowLabel = (id: string): string => chapterLabel(id)
  const colLabel = (id: string): string => elementLabel(id)
  const rowCount = (id: string): number => chapterCount(id)
  const colCount = (id: string): number => elementCount(id)

  // --- hover summaries ---
  const chapterSectionsOrdered = (chapterId: string): Array<{ key: string; title: string }> => {
    const keys =
      chapterId === NOCHAP ? draft.unattachedChapter : (draft.chapters.find((c) => c.id === chapterId)?.sections ?? [])
    return keys.map((k) => ({ key: k, title: entryByKey.get(k)?.displayText ?? k }))
  }

  const renderChapterTooltip = (chapterId: string): ReactNode => {
    const sections = chapterSectionsOrdered(chapterId)
    return (
      <Box dir={bookDirection} sx={{ maxHeight: 360, overflowY: 'auto' }}>
        <Typography sx={{ fontSize: 11.5, fontWeight: 700, mb: 0.5 }}>{chapterLabel(chapterId)}</Typography>
        {sections.length === 0 ? (
          <Typography sx={{ fontSize: 12, opacity: 0.7 }}>אין מקטעים</Typography>
        ) : (
          sections.map((s, i) => (
            <Box key={s.key} sx={{ display: 'flex', gap: 0.75, py: 0.125 }}>
              <Typography component="span" sx={{ fontSize: 11.5, opacity: 0.55, minWidth: 18, textAlign: 'end' }}>
                {i + 1}.
              </Typography>
              <Typography component="span" sx={{ fontSize: 12 }}>
                {s.title}
              </Typography>
            </Box>
          ))
        )}
      </Box>
    )
  }

  const renderPanel = (): ReactNode => {
    if (!panelElementId) {
      return null
    }
    const elementId = panelElementId
    const wantElem = elementId === NOELEM ? null : elementId
    // One row per section, unassigned (no chapter) first, then by chapter order.
    const rows = allKeys
      .filter((k) => (draft.elementOf[k] ?? null) === wantElem)
      .map((k) => ({ key: k, chap: chapterOfKey(k) }))
      .sort((a, b) => {
        const ai = a.chap === NOCHAP ? -1 : draft.chapters.findIndex((c) => c.id === a.chap)
        const bi = b.chap === NOCHAP ? -1 : draft.chapters.findIndex((c) => c.id === b.chap)
        return ai - bi
      })
    return (
      <Box
        dir={bookDirection}
        sx={{
          width: 'max-content',
          minWidth: 200,
          maxWidth: 'min(92vw, 460px)',
          maxHeight: '72vh',
          overflowY: 'auto',
          bgcolor: raisedBg,
          border: '1px solid',
          borderColor: 'divider',
          borderRadius: 1,
          boxShadow: 8,
          p: 1.25,
        }}
      >
        <Typography sx={{ fontSize: 13.5, fontWeight: 700, mb: 1 }}>{elementLabel(elementId)}</Typography>
        {rows.length === 0 ? (
          <Typography sx={{ fontSize: 12, opacity: 0.6 }}>אין מקטעים</Typography>
        ) : (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.4 }}>
            {rows.map(({ key, chap }) => {
              const entry = entryByKey.get(key)
              if (!entry) {
                return null
              }
              return (
                <PanelRow
                  key={key}
                  sectionKey={key}
                  title={entry.displayText}
                  sourceLabel={entry.chapter.title?.trim() || entry.chapter.filename}
                  chapterText={chap === NOCHAP ? null : chapterLabel(chap)}
                  selected={selectedKeys.has(key)}
                  draggedAway={activeId !== null && draggedSet.has(key) && activeId !== PANEL_PREFIX + key}
                  onSelect={toggleSelection}
                  onOpen={() => openPreview(key)}
                  onUnassign={() => unassignKey(key)}
                />
              )
            })}
          </Box>
        )}
      </Box>
    )
  }

  const gridTemplateColumns =
    `${COL_LABEL_W}px ` + columnIds.map((id) => `${id === NOELEM ? COL_UNCH_W : COL_CHAP_W}px`).join(' ')
  const headerSx = {
    position: 'sticky' as const,
    top: 0,
    zIndex: 3,
    bgcolor: labelBg,
    borderInlineEnd: '1px solid',
    borderBottom: '2px solid',
    borderColor: 'divider',
    px: 0.75,
    py: 0.75,
  }
  const labelSx = {
    position: 'sticky' as const,
    insetInlineStart: 0,
    zIndex: 2,
    height: ROW_H,
    bgcolor: labelBg,
    borderInlineEnd: '2px solid',
    borderBottom: '1px solid',
    borderColor: 'divider',
    px: 0.75,
    display: 'flex',
    flexDirection: 'row' as const,
    alignItems: 'center',
    gap: 0.5,
  }

  return (
    <>
      <Dialog open={open} onClose={handleClose} fullScreen PaperProps={{ sx: { bgcolor: 'background.default' } }}>
        <Box dir={bookDirection} sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
          <Box sx={{ flex: 1, overflow: 'auto' }}>
            <DndContext
              sensors={sensors}
              collisionDetection={detectCollisions}
              onDragStart={onDragStart}
              onDragEnd={onDragEnd}
            >
              <Box sx={{ display: 'grid', gridTemplateColumns, width: 'max-content', minWidth: '100%' }}>
                {/* Header row */}
                <Box sx={{ ...headerSx, insetInlineStart: 0, zIndex: 4 }}>
                  <Typography sx={{ fontSize: 12, fontWeight: 600, color: 'text.secondary' }}>פרק \ יסוד</Typography>
                </Box>
                {columnIds.map((colId) =>
                  colId !== NOELEM ? (
                    <ElementAxisItem
                      key={`h-${colId}`}
                      id={colId}
                      name={colLabel(colId)}
                      count={colCount(colId)}
                      onCountClick={openPanel}
                      containerSx={headerSx}
                      editing={editingId === colId}
                      editValue={editValue}
                      isActiveDrag={activeId === colId}
                      active={panelElementId === colId}
                      onStartEdit={startEdit}
                      onChangeEdit={setEditValue}
                      onCommitEdit={commitEdit}
                      onCancelEdit={cancelEdit}
                    />
                  ) : (
                    <Box
                      key={`h-${colId}`}
                      sx={{
                        ...headerSx,
                        ...(panelElementId === colId && {
                          bgcolor: (theme: Theme) => alpha(theme.palette.primary.main, 0.16),
                        }),
                      }}
                    >
                      <Typography
                        title={colLabel(colId)}
                        sx={{
                          fontSize: 12.5,
                          fontWeight: 600,
                          color: panelElementId === colId ? 'primary.main' : undefined,
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          whiteSpace: 'nowrap',
                        }}
                      >
                        {colLabel(colId)}
                      </Typography>
                      <Typography
                        data-element-id={colId}
                        onClick={(e) => openPanel(colId, e.currentTarget)}
                        sx={{
                          fontSize: 11,
                          color: 'text.secondary',
                          width: 'fit-content',
                          cursor: 'pointer',
                          borderRadius: 0.5,
                          px: 0.5,
                          '&:hover': { bgcolor: 'action.hover', color: 'text.primary' },
                        }}
                      >
                        {colCount(colId)}
                      </Typography>
                    </Box>
                  ),
                )}

                {/* Body rows */}
                {rowIds.map((rowId) => (
                  <Box key={`row-${rowId}`} sx={{ display: 'contents' }}>
                    <ChapterRowLabel
                      chapterId={rowId}
                      label={rowLabel(rowId)}
                      count={rowCount(rowId)}
                      tooltip={<LazyTooltipBody build={() => renderChapterTooltip(rowId)} />}
                      labelSx={labelSx}
                    />
                    {columnIds.map((colId) => {
                      const elementId = colId
                      const chapterId = rowId
                      const keys = cellKeysFor(elementId, chapterId)
                      return (
                        <MatrixCell
                          key={cellId(elementId, chapterId)}
                          id={cellId(elementId, chapterId)}
                          expandable={keys.length > 1}
                        >
                          {keys.map((key) => {
                            const entry = entryByKey.get(key)
                            if (!entry) {
                              return null
                            }
                            return (
                              <MatrixChip
                                key={key}
                                sectionKey={key}
                                label={entry.displayText}
                                selected={selectedKeys.has(key)}
                                draggedAway={activeId !== null && key !== activeId && draggedSet.has(key)}
                                onSelect={toggleSelection}
                                onOpen={() => openPreview(key)}
                              />
                            )
                          })}
                        </MatrixCell>
                      )
                    })}
                  </Box>
                ))}
              </Box>

              <DragOverlay>
                {activeElement ? (
                  <Box
                    sx={{
                      opacity: 0.95,
                      pointerEvents: 'none',
                      px: 1,
                      py: 0.5,
                      borderRadius: 1,
                      border: '1px solid',
                      borderColor: 'primary.main',
                      bgcolor: 'background.paper',
                      maxWidth: 220,
                    }}
                  >
                    <Typography
                      sx={{
                        fontSize: 12.5,
                        fontWeight: 600,
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                      }}
                    >
                      {activeElement.name}
                    </Typography>
                  </Box>
                ) : activeEntry ? (
                  <Box
                    sx={{
                      position: 'relative',
                      opacity: 0.95,
                      pointerEvents: 'none',
                      px: 0.75,
                      py: 0.25,
                      borderRadius: 0.75,
                      border: '1px solid',
                      borderColor: 'primary.main',
                      bgcolor: 'background.paper',
                      maxWidth: 220,
                    }}
                  >
                    <Typography
                      sx={{ fontSize: 11.5, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
                    >
                      {activeEntry.displayText}
                    </Typography>
                    {dragCount > 1 && (
                      <Chip
                        label={dragCount}
                        size="small"
                        color="primary"
                        sx={{ position: 'absolute', top: -10, insetInlineEnd: -10, height: 22, fontWeight: 700 }}
                      />
                    )}
                  </Box>
                ) : null}
              </DragOverlay>

              <Popper
                open={Boolean(panelElementId)}
                anchorEl={panelAnchorEl}
                placement="bottom-end"
                modifiers={[{ name: 'offset', options: { offset: [0, 6] } }]}
                sx={{ zIndex: (theme) => theme.zIndex.tooltip }}
              >
                <ClickAwayListener onClickAway={closePanel}>
                  <Box>{renderPanel()}</Box>
                </ClickAwayListener>
              </Popper>
            </DndContext>
          </Box>

          <Box
            sx={{
              display: 'flex',
              justifyContent: 'flex-end',
              gap: 1,
              px: 2,
              py: 1.25,
              borderTop: '1px solid',
              borderColor: 'divider',
            }}
          >
            <Button color="inherit" onClick={handleClose}>
              ביטול
            </Button>
            <Button variant="contained" onClick={handleSave} disabled={!dirty || saving}>
              שמירה
            </Button>
          </Box>
        </Box>
      </Dialog>
      {previewKey && (
        <Dialog
          open
          onClose={closePreview}
          maxWidth={false}
          // Above the per-element panel (a Popper at tooltip z-index), so the editor opens over it.
          sx={{ zIndex: (theme) => theme.zIndex.tooltip + 10 }}
          PaperProps={{ sx: { width: 'min(72vw, 920px)', height: 'min(78vh, 760px)', direction: bookDirection } }}
        >
          <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden', height: '100%' }}>
            <SearchPreviewPanel
              previewIdentity={previewKey}
              previewTitle={previewTitle}
              previewAst={previewAst}
              previewLoading={previewLoading}
              previewError={previewError}
              book={book ?? null}
              editable={!previewError && !previewLoading && Boolean(previewAst)}
              onChange={onPreviewChange}
              onSave={onPreviewSave}
              saving={previewSaving}
              hasUnsavedChanges={previewDirty}
              recentlySaved={previewRecentlySaved}
            />
          </Box>
        </Dialog>
      )}
    </>
  )
}
